import React, { useRef } from 'react'
import QRCode from 'react-qr-code'
import { jsPDF } from 'jspdf'
import QRCodeGenerator from 'qrcode'
import { useTranslation } from 'react-i18next'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import 'styles/QRModal.css'
import { QR_TYPES } from 'constants/qr.types'

const QRModal = ({ isOpen, onClose, worker }) => {
  const { t } = useTranslation()
  const qrRef = useRef(null)

  const handlePrintQR = async () => {
    try {
      // Generate QR code as data URL
      const workerData = {
        qrType: QR_TYPES.WORKER,
        id: worker.workerId,
        name: worker.firstName,
        lastName: worker.lastName,
        phone: worker.phoneNumber,
        oldId: worker.oldId
      }

      const qrDataUrl = await QRCodeGenerator.toDataURL(
        JSON.stringify(workerData),
        {
          margin: 1,
          width: 256,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        }
      )

      // Create PDF with sticker dimensions
      const CM_TO_MM = 10
      const PAGE_WIDTH = 4 * CM_TO_MM // 40mm
      const PAGE_HEIGHT = 5 * CM_TO_MM // 50mm
      const MARGIN = 2 // 2mm margin for more space

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [PAGE_WIDTH, PAGE_HEIGHT]
      })

      const fullName = `${worker.firstName} ${worker.lastName}`
      const workerId = worker.oldId || worker.workerId

      // Worker name — top, bold green
      pdf.setFontSize(7)
      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(5, 81, 13)
      pdf.text(fullName, PAGE_WIDTH / 2, MARGIN + 3, { align: 'center' })

      // Worker ID — below name, smaller
      pdf.setFontSize(5)
      pdf.setFont('helvetica', 'normal')
      pdf.setTextColor(0, 0, 0)
      pdf.text(`ID: ${workerId}`, PAGE_WIDTH / 2, MARGIN + 6.5, { align: 'center' })

      // QR Code
      const qrSize = 26
      const qrX = (PAGE_WIDTH - qrSize) / 2
      const qrY = MARGIN + 9
      pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize)

      // Filename: worker_Juan-Garcia_W001_2026-08-13.pdf
      const slug = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')
      const date = new Date(worker.updatedAt || worker.createdAt).toISOString().slice(0, 10)
      const filename = `worker_${slug(fullName)}_${slug(String(workerId))}_${date}.pdf`
      pdf.save(filename)
    } catch (err) {
      console.error('Error generating PDF:', err)
    }
  }

  if (!worker) return null

  const workerData = {
    qrType: QR_TYPES.WORKER,
    id: worker.workerId,
    name: worker.firstName,
    lastName: worker.lastName,
    phone: worker.phoneNumber,
    oldId: worker.oldId
  }

  const customFooter = (
    <>
      <ButtonComponent onClick={handlePrintQR} className='bg-green-600 text-white hover:bg-green-700'>
        {t('mgmt.qrModal.printQr')}
      </ButtonComponent>
      <ButtonComponent onClick={onClose} customColorsStyles='bg-gray-100 text-greenPrimary hover:text-white'>
        {t('mgmt.common.close')}
      </ButtonComponent>
    </>
  )

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('mgmt.qrModal.title')}
      customFooter={customFooter}
    >
      <div className='qr-content'>
        <div className='worker-info mb-4'>
          <h4 className='text-lg font-semibold'>{worker.firstName} {worker.lastName}</h4>
          <p className='text-gray-600'>ID: {worker.oldId || worker.workerId}</p>
        </div>

        <div className='qr-container mb-4' ref={qrRef}>
          <QRCode
            value={JSON.stringify(workerData)}
            size={256}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            viewBox='0 0 256 256'
          />
        </div>

        <div className='qr-description text-sm text-gray-500 text-center'>
          {t('mgmt.qrModal.scanHelp')}
        </div>
      </div>
    </ModalComponent>
  )
}

export default QRModal
